import {
  LayoutGrid,
  MessageSquareText,
  Sun,
  Timer,
  X,
  Menu,
} from "lucide-react";
import React, { useState } from "react";
import { NavLink } from "react-router-dom";
import "../styles/scrollbar.css";
import { useAuth } from "../auth/AuthContext";
import { AESTHETICS_ENABLED } from "../config/aesthetics";

interface SidebarItem {
  id: string;
  label: string;
  icon: string | React.ElementType;
  path: string;
}

const allMainItems: SidebarItem[] = [
  {
    id: "dashboard",
    label: "Dashboard",
    icon: "/bars/dashboard.svg",
    path: "/dashboard",
  },
  {
    id: "calendar",
    label: "Calendar",
    icon: "/bars/calendar.svg",
    path: "/calendar",
  },
  {
    id: "bookings",
    label: "Bookings",
    icon: "/bars/bookings.svg",
    path: "/bookings",
  },
  { id: "pos", label: "POS", icon: "/bars/pos.svg", path: "/pos" },
  {
    id: "website",
    label: "Website",
    icon: "/bars/website.svg",
    path: "/website",
  },
  { id: "manage", label: "Manage", icon: LayoutGrid, path: "/manage" },
  {
    id: "customers",
    label: "Customers",
    icon: "/bars/customers.svg",
    path: "/customers",
  },
  {
    id: "consultation",
    label: "Consultation",
    icon: MessageSquareText,
    path: "/consultation",
  },
  {
    id: "sunbeds",
    label: "Sunbeds",
    icon: Sun,
    path: "/sunbeds",
  },
  {
    id: "sunbed-session",
    label: "Sunbed Session",
    icon: Timer,
    path: "/sunbed-session",
  },
  {
    id: "reports",
    label: "Reports",
    icon: "/bars/reports.svg",
    path: "/reports",
  },
];

const footerItem: SidebarItem = {
  id: "settings",
  label: "Settings",
  icon: "/bars/settings.svg",
  path: "/settings",
};

interface SidebarProps {
  isOpen?: boolean;
  onClose?: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ isOpen = false, onClose }) => {
  const { user } = useAuth();
  const [isTabletExpanded, setIsTabletExpanded] = useState(false);

  const isSunbedEnabled = React.useMemo(() => {
    const setting = user?.company?.is_sunbed;
    if (setting === undefined || setting === null) return false;
    if (typeof setting === 'string') return setting === 'active';
    return Number(setting) === 1;
  }, [user]);

  const filteredItems = allMainItems.filter((item: SidebarItem) => {
    if (item.id === "sunbeds" || item.id === "sunbed-session") {
      return isSunbedEnabled;
    }
    return true;
  });

  const renderNavLink = (item: SidebarItem) => {
    const isString = typeof item.icon === "string";
    const IconComponent = !isString ? (item.icon as React.ElementType) : null;

    return (
      <NavLink
        key={item.id}
        to={item.path}
        onClick={onClose}
        className={({ isActive }) =>
          `flex items-center space-x-3 px-3 py-2 rounded-md cursor-pointer transition
           md:px-2 md:space-x-0 lg:justify-start lg:px-3 lg:space-x-3
           ${isTabletExpanded
            ? "md:justify-start md:space-x-3 md:px-3"
            : "md:justify-center"
          }
          ${isActive
            ? "bg-purple-100 text-purple-700 font-semibold"
            : "text-black hover:bg-gray-100"
          }`
        }
      >
        {({ isActive }) => (
          <>
            <div
              className={`w-9 h-9 flex items-center justify-center rounded-md shrink-0
              ${isActive ? "bg-purple-600" : "bg-gray-100"}`}
            >
              {isString ? (
                <img
                  src={item.icon as string}
                  alt={item.label}
                  className={`w-5 h-5 ${isActive ? "filter brightness-0 invert" : ""
                    }`}
                />
              ) : (
                IconComponent && (
                  <IconComponent
                    size={20}
                    className={isActive ? "text-white" : "text-purple-600"}
                  />
                )
              )}
            </div>
            <span
              className={`text-sm block lg:block ${isTabletExpanded ? "md:block" : "md:hidden"
                }`}
            >
              {item.label}
            </span>
          </>
        )}
      </NavLink>
    );
  };

  return (
    <>
      {/* Mobile Overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar Container */}
      <div
        className={`bg-white flex flex-col border-r border-gray-200 transition-all duration-300
          md:translate-x-0 md:static md:h-screen md:flex md:shadow-none
          lg:w-56 lg:p-0
          ${isTabletExpanded ? "md:w-56" : "md:w-[70px]"}
          fixed inset-y-0 left-0 z-50 h-full w-64 shadow-2xl p-4 md:p-4 lg:p-0
          ${isOpen ? "translate-x-0" : "-translate-x-full"}
        `}
      >
        <div className="flex flex-col h-full overflow-hidden">
          {/* Close button for mobile */}
          <div className="flex justify-end mb-4 md:hidden">
            <button
              onClick={onClose}
              className="p-2 text-gray-500 hover:bg-gray-100 rounded-md"
            >
              <X size={20} />
            </button>
          </div>

          {/* Tablet Toggle Button */}
          <div
            className={`hidden md:flex lg:hidden mb-4 ${isTabletExpanded ? "justify-start px-0" : "justify-center"
              }`}
          >
            <button
              onClick={() => setIsTabletExpanded(!isTabletExpanded)}
              className={`flex items-center space-x-3 py-2 rounded-md cursor-pointer transition hover:bg-gray-100 ${isTabletExpanded ? "px-3 w-full" : "px-2 justify-center"
                }`}
            >
              <div className="w-9 h-9 flex items-center justify-center rounded-md shrink-0 bg-gray-100">
                {isTabletExpanded ? (
                  <X size={20} className="text-purple-600" />
                ) : (
                  <Menu size={20} className="text-purple-600" />
                )}
              </div>
              {isTabletExpanded && (
                <span className="text-sm font-semibold">Menu</span>
              )}
            </button>
          </div>

          <div className="flex-1 overflow-y-auto space-y-2 lg:p-4 sidebar-scrollbar hide-scrollbar lg:sidebar-scrollbar">
            <nav className="space-y-2">{filteredItems.map(renderNavLink)}</nav>
          </div>

          <div className="p-4 border-t border-gray-100 lg:p-4 lg:border-t lg:border-gray-100">
            {renderNavLink(footerItem)}
          </div>
        </div>
      </div>
    </>
  );
};

export default Sidebar;
